/**
 * authorizedFetch - the one correct way to hand-roll an API call.
 *
 * WHY THIS FILE EXISTS (v4.5.2)
 * =============================
 * Five components were making raw `fetch()` calls and reading the JWT
 * out of localStorage themselves. Every one of them read the wrong
 * property:
 *
 *     const parsed = JSON.parse(localStorage.getItem("tnn.auth"));
 *     headers: parsed?.access ? { Authorization: `Bearer ${parsed.access}` } : {}
 *                     ^^^^^^ undefined
 *
 * The auth slice persists its own state via `JSON.stringify(state)`, and
 * that state's field is `accessToken`, not `access`. (`access` is the key
 * on the *login response body*, which is where the mistake came from.)
 * So `parsed.access` was always undefined, the ternary fell through to
 * `{}`, and the request went out with no Authorization header at all.
 *
 * The failure was invisible: each caller did `res.ok ? res.json() : []`
 * and then rendered nothing when the list was empty. A 401 looked
 * exactly like "no activity yet." Five features shipped dead:
 * PendingActionsBar, PilotAnalyticsPage, TeamActivityFeed,
 * WhileYouWereAway, and bulk role assignment on PeopleListPage.
 *
 * These calls were ALSO missing `X-Tenant-Slug`. `baseApi.prepareHeaders`
 * sends it on every RTK Query request, and TenantResolutionMiddleware
 * needs it to establish tenant context - without it, tenant-scoped
 * querysets resolve to none() and the endpoint returns an empty list
 * even with a valid token. So fixing the token alone was not enough.
 *
 * PREFER RTK QUERY. This helper exists for the handful of call sites
 * that are not yet migrated. New endpoints belong in an api slice, which
 * gets the tenant header, the auth header, and - the part this helper
 * genuinely cannot replicate - `baseQueryWithReauth`'s silent token
 * refresh on 401. A raw call made 61 minutes into a shift still fails.
 * That is the remaining reason to finish the migration.
 *
 * URL HELPERS
 * ===========
 * - `apiV1Base()` - ``${env.apiBaseUrl}/api/v1`` for RTK `baseApi.baseUrl`
 * - `v1Url(path)` - full absolute URL for raw `fetch` / `href` (split-host staging)
 * - `publicUrl(path)` - root `/public/*` endpoints (referral portal, etc.)
 */

import { env } from "@/shared/config/env";
import { resolveRequestTenantSlug } from "@/shared/lib/tenantSlug";

const STORAGE_KEY = "tnn.auth";

interface PersistedAuth {
  accessToken?: string | null;
  /** Legacy/defensive: some older writers used the response-body key. */
  access?: string | null;
  tenantSlug?: string | null;
  user?: unknown;
}

/** Read persisted auth state, preferring localStorage then sessionStorage. */
export function readPersistedAuth(): PersistedAuth | null {
  try {
    const raw =
      window.localStorage.getItem(STORAGE_KEY) ||
      window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? (parsed as PersistedAuth) : null;
  } catch {
    // Malformed JSON or storage blocked (private mode, embedded webview).
    return null;
  }
}

/** The access token, tolerating either field name. */
export function readAccessToken(): string | null {
  const parsed = readPersistedAuth();
  return parsed?.accessToken ?? parsed?.access ?? null;
}

/**
 * Build the headers baseApi would have sent: Authorization + X-Tenant-Slug.
 * Caller-supplied headers win, so a caller can override Content-Type.
 */
export function authHeaders(extra: Record<string, string> = {}): Record<string, string> {
  const parsed = readPersistedAuth();
  const token = parsed?.accessToken ?? parsed?.access ?? null;
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  const slug = resolveRequestTenantSlug({
    accessToken: token,
    user: parsed?.user ?? null,
    tenantSlug: parsed?.tenantSlug ?? null,
  });
  if (slug) headers["X-Tenant-Slug"] = slug;
  return { ...headers, ...extra };
}

/** RTK Query base URL: API host + `/api/v1`. */
export function apiV1Base(): string {
  return `${env.apiBaseUrl}/api/v1`;
}

/**
 * Absolute URL for a v1 API path (ad-hoc fetch, download links).
 * `path` may omit the leading slash (e.g. `people/screenings/`).
 */
export function v1Url(path: string): string {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${apiV1Base()}${normalized}`;
}

/** Absolute URL for Django routes mounted at ``/public/`` (not under ``/api/v1``). */
export function publicUrl(path: string): string {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${env.apiBaseUrl}${normalized}`;
}

/**
 * `fetch` with auth and tenant headers attached.
 *
 * Throws nothing extra - it is a thin wrapper, so callers keep their
 * existing `res.ok` handling. What changes is that `res.ok` is now
 * true when it should be.
 */
export function authorizedFetch(
  input: string,
  init: RequestInit = {},
): Promise<Response> {
  const supplied = (init.headers as Record<string, string> | undefined) ?? {};
  return fetch(input, { ...init, headers: authHeaders(supplied) });
}

/** Download a CSV from an authenticated v1 path. Throws when the request fails. */
export async function downloadAuthorizedCsv(path: string, filename: string): Promise<void> {
  const res = await authorizedFetch(v1Url(path));
  if (!res.ok) throw new Error(String(res.status));
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default authorizedFetch;
